import {
  Box,
  ButtonGroup,
  Card,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import Dialog from "components/NoScrollDialog";
import DropdownSelect from "components/DropdownSelect";
import LoadingButton from "components/LoadingButton";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import PillarsTable from "components/PillarsTable";
import { useAuth } from "contexts/auth.context";
import { useLanguage } from "contexts/language.context";
import { useCallback, useEffect, useMemo, useState } from "react";
import accountsService from "services/accounts-service";
import categoriesService from "services/categories-service";
import pillarsService from "services/pillars-service";

export default function TemplateDialog({
  open,
  onClose,
  onSubmit,
  mode,
  initial,
}) {
  const [value, setValue] = useState({});
  const [updated, setUpdated] = useState({});
  const [loading, setLoading] = useState({ save: false });
  const [pillars, setPillars] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [tab, setTab] = useState(0);
  const auth = useAuth();
  const { label } = useLanguage();

  async function handleFetchPillars(subcategoryId, auditTypeId) {
    const [response, data] = await pillarsService.getPillars({
      subcategoryId,
      auditTypeId: auditTypeId == 2 ? 2 : null,
    });
    if (response.ok) {
      setPillars(data.filter((v) => v.kpis.length));
    }
  }

  async function handleFetchSubcategories() {
    const [response, data] = await categoriesService.getCategories({
      raw: true,
    });
    if (response.ok) {
      setSubcategories(data);
    }
  }

  async function handleFetchAccounts() {
    const [response, data] = await accountsService.getAccounts({ raw: true });
    if (response.ok) {
      setAccounts(data);
    }
  }

  const handleChange = useCallback(
    (field, value) => {
      setValue((curr) => ({ ...curr, [field]: value }));
      setUpdated((curr) => ({ ...curr, [field]: value }));
    },
    [setValue, setUpdated],
  );

  const onChangeSelected = useCallback(
    (templateKpis) => {
      handleChange("templateKpis", templateKpis);
    },
    [handleChange],
  );

  function handleChangeLoading(field, value) {
    setLoading((curr) => ({ ...curr, [field]: value }));
  }

  const initialProp = useMemo(() => {
    return initial?.templateKpis || [];
  }, [initial]);

  const dropdownOptions = useMemo(() => {
    return {
      subcategories:
        subcategories?.map((subcategory) => {
          return {
            id: subcategory.id,
            label: subcategory.description,
            category: subcategory.category.description,
          };
        }) || [],

      accounts:
        accounts?.map((account) => {
          return {
            id: account.id,
            label: account.name,
            isExternalAuditor: account.isExternalAuditor,
            externalAuditorId:
              account.externalSubscription?.account?.id || null,
          };
        }) || [],
    };
  }, [subcategories, accounts]);

  useEffect(() => {
    handleFetchSubcategories();
    handleFetchAccounts();
  }, []);

  useEffect(() => {
    if (mode === "create") {
      if (initial) {
        setValue({
          name: initial.name,
          subcategoryId: initial.subcategoryId,
          templateKpis: initial.templateKpis,
        });
        setUpdated({
          name: initial.name,
          subcategoryId: initial.subcategoryId,
          templateKpis: initial.templateKpis.map((templateKpi) => ({
            kpiId: templateKpi.kpi.id,
            description: templateKpi.description,
          })),
        });
      } else {
        setValue({ name: "", templateKpis: [] });
        setUpdated({});
      }
    } else if (mode === "update") {
      setValue(initial);
      setUpdated({});
    }
  }, [mode, initial]);

  useEffect(() => {
    if (value.subcategoryId) {
      handleFetchPillars(value.subcategoryId, value.auditTypeId);
    } else {
      setPillars([]);
    }
  }, [value.subcategoryId, value.auditTypeId, mode]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      PaperProps={{
        component: "form",
        onSubmit: async (event) => {
          event.preventDefault();

          if (mode == "create" && tab == 0) {
            setTab(1);
            return;
          }

          handleChangeLoading("save", true);
          const response = await onSubmit({ id: value.id, ...updated });
          if (response.success) {
            onClose();
          }
          handleChangeLoading("save", false);
        },
      }}
      fullWidth
    >
      <DialogTitle>
        {mode === "create"
          ? initial
            ? label("duplicate-template")
            : label("Create New Template")
          : mode === "request"
            ? `${label("Request Template from")} ${auth.user?.account?.externalSubscription?.account?.name || ""}`
            : label("Edit Template")}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={tab}
            onChange={(e, newValue) => {
              setTab(newValue);
            }}
          >
            <Tab label="Info" id="info" />
            <Tab label="Pillars & Kpis" id="kpis" />
          </Tabs>
        </Box>

        <Box>
          {tab == 0 ? (
            <Box sx={{ py: 2 }}>
              <Grid container spacing={2} alignItems="flex-start">
                <Grid
                  item
                  container
                  xs={12}
                  spacing={2}
                  alignItems="flex-start"
                  sx={{ height: "auto" }}
                >
                  <Grid container item xs={12} spacing={2}>
                    <Grid item xs>
                      <MDInput
                        label={label("name-singular")}
                        fullWidth
                        value={value.name || ""}
                        onChange={(e) => {
                          handleChange("name", e.target.value);
                        }}
                        required
                      />
                    </Grid>
                    <Grid item>
                      <ButtonGroup>
                        <MDButton
                          id="audit-auditTypeId-0"
                          variant={!value.auditTypeId ? "gradient" : "outlined"}
                          color={!value.auditTypeId ? "info" : "secondary"}
                          size="medium"
                          sx={
                            !value.auditTypeId
                              ? (theme) => ({
                                  borderColor: theme.borders.borderColor,
                                })
                              : null
                          }
                          onClick={() => {
                            handleChange("auditTypeId", 0);
                          }}
                        >
                          {label("normal-audit")}
                        </MDButton>
                        <MDButton
                          id="audit-auditTypeId-1"
                          variant={
                            value.auditTypeId === 1 ? "gradient" : "outlined"
                          }
                          color={value.auditTypeId === 1 ? "info" : "secondary"}
                          size="medium"
                          sx={
                            value.auditTypeId === 1
                              ? (theme) => ({
                                  borderColor: theme.borders.borderColor,
                                })
                              : null
                          }
                          onClick={() => {
                            handleChange("auditTypeId", 1);
                          }}
                        >
                          {label("scoreless-audit")}
                        </MDButton>

                        <MDButton
                          id="audit-auditTypeId-2"
                          variant={
                            value.auditTypeId === 2 ? "gradient" : "outlined"
                          }
                          color={value.auditTypeId === 2 ? "info" : "secondary"}
                          size="medium"
                          sx={
                            value.auditTypeId === 2
                              ? (theme) => ({
                                  borderColor: theme.borders.borderColor,
                                })
                              : null
                          }
                          onClick={() => {
                            handleChange("auditTypeId", 2);
                          }}
                        >
                          Mystery
                        </MDButton>
                      </ButtonGroup>
                    </Grid>
                  </Grid>

                  <Grid item xs={6}>
                    <DropdownSelect
                      label={label("Category-singular")}
                      id="category-selector"
                      initialValue={
                        value.subcategoryId == 0 || !value.subcategoryId
                          ? ""
                          : value.subcategoryId
                      }
                      onChange={(newValue) => {
                        handleChange("subcategoryId", newValue?.id);
                      }}
                      inputProps={
                        mode === "create"
                          ? {
                              helperText: label("template-initial-category"),
                            }
                          : null
                      }
                      items={dropdownOptions.subcategories}
                      required={true}
                      groupBy={(option) => option.category}
                      fullWidth
                      readOnly={mode === "update"}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <DropdownSelect
                      label={label("account-singular")}
                      initialValue={!value.accountId ? "" : value.accountId}
                      onChange={(newValue) => {
                        handleChange("accountId", newValue?.id);
                      }}
                      items={dropdownOptions?.accounts}
                      required={true}
                      fullWidth
                    />
                  </Grid>
                </Grid>

                {value.accountId &&
                dropdownOptions?.accounts?.find(
                  (account) => account.id == value.accountId,
                )?.isExternalAuditor ? (
                  <Grid item container xs={12} sx={{ alignSelf: "flex-start" }}>
                    <Grid item>
                      <FormControl sx={{ pl: 1, pr: 4 }} required>
                        <FormLabel
                          sx={{
                            fontSize: "16px",
                            fontWeight: "400",
                            mb: 1,
                          }}
                        >
                          Share with
                        </FormLabel>
                        <RadioGroup
                          value={value.shareWith == 1 ? "accounts" : "everyone"}
                          onChange={(e) => {
                            handleChange(
                              "shareWith",
                              e.target.value == "accounts" ? 1 : 0,
                            );
                          }}
                          sx={{ width: "100%" }}
                        >
                          <FormControlLabel
                            value="everyone"
                            control={<Radio />}
                            label="Every client"
                          />
                          <FormControlLabel
                            value="accounts"
                            control={<Radio />}
                            label="Select clients"
                            sx={{ marginTop: -1 }}
                          />
                        </RadioGroup>
                      </FormControl>
                    </Grid>
                    <Grid item xs>
                      {value.shareWith == 1 ? (
                        <DropdownSelect
                          label={label("account-plural")}
                          initialValue={
                            value.shareWithAccounts?.map(
                              (account) => account.id,
                            ) || []
                          }
                          onChange={(newValue) => {
                            handleChange("shareWithAccounts", newValue);
                          }}
                          multiple
                          items={dropdownOptions?.accounts.filter(
                            (account) =>
                              account.externalAuditorId == value.accountId,
                          )}
                          // required={true}
                          fullWidth
                          inputProps={{ fullWidth: true }}
                        />
                      ) : null}
                    </Grid>
                  </Grid>
                ) : null}
              </Grid>
            </Box>
          ) : null}

          {tab == 1 ? (
            <Box
              sx={{ py: 2, overflow: "auto", height: "calc(100vh - 300px)" }}
            >
              <PillarsTable
                pillars={pillars}
                initial={initialProp}
                onChangeSelected={onChangeSelected}
                updatableDescription
                updatableWeight
                updatableEliminatory
                include={{ weight: true, checkbox: true, eliminatory: true }}
              />
            </Box>
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions>
        <MDButton
          onClick={() => {
            onClose();
          }}
        >
          {label("cancel-button")}
        </MDButton>
        <LoadingButton
          type="submit"
          variant="gradient"
          color="info"
          loading={loading.save}
        >
          {mode == "create" && tab == 0
            ? label("next-button")
            : label("save-button")}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
